import React, { useState, useEffect } from "react";
import { ReactComponent as ArrowBackIcon } from "assets/images/common/svgs/arrow_back.svg";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import useCupOfGratitude from "../useHooks";
import { CreateCupOfGratitudeDTO } from "utils/helpers/models/cup-gratitude/cup-gratitude.dto";
import useUtils from "hooks/useUtils";
import { useSelector } from "react-redux";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { warningToaster } from "utils/helpers/common/alert-service";

type FormData = {
  name: string;
  price: number;
};

const CupGratitudeEdit: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: { name: "" },
  });
  const [data, setData] = useState<any>(null); // Changed to null instead of array
  const { getQueryParams } = useUtils();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const params = getQueryParams();
  const cupId = params?.id;

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const navigate = useNavigate();
  const { uploadFile, getCupOfGratitudesById, editCupOfGratitude } =
    useCupOfGratitude();

  const goToBack = () => navigate(-1);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  useEffect(() => {
    getCupOfGratitudesById(cupId, setData);
  }, [cupId]);

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        price: data.price,
      });
      if (data.image) {
        const imageUrl = concatPathWithBackendUrl(data.image.name);
        setImagePreview(imageUrl);
      }
    }
  }, [data, reset]);

  const onSubmit = async (formData: FormData) => {
    if (!selectedFile && !imagePreview) {
      warningToaster("Image is required!");
      return;
    }
    const updatedData: any = {
      ...formData,
      id: data.id,
      imageId: data.imageId, 
      price: Number(formData.price),
    };
    if (selectedFile) {
      const formDataToUpload = new FormData();
      formDataToUpload.append("file", selectedFile);
      const uploadedImage: any = await uploadFile(formDataToUpload);
      updatedData.imageId = uploadedImage.id;
    }
    console.log(updatedData, "updatedData");
    await editCupOfGratitude(params?.id, updatedData);
  };

  return (
    <div>
      <h1 className="text-base text-primaryText font-bold flex items-center gap-3">
        <span
          onClick={goToBack}
          className="bg-transparentBlueBg w-[44px] h-[44px] rounded-full flex items-center justify-center cursor-pointer"
        >
          <ArrowBackIcon />
        </span>
        Edit Cup of Gratitude
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
        <div className="bg-white p-6 rounded-lg space-y-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Name */}
            <div className="flex-1 flex flex-col gap-1 w-full sm:max-w-[230px]">
              <label className="text-labelText font-semibold">
                Cup of Gratitude Name
              </label>
              <input
                type="text"
                placeholder="Enter name"
                {...register("name", { required: "Cup name is required" })}
                className="w-full h-[51px] px-4 border border-lightGrayBorder rounded-lg outline-none"
              />
              {errors.name && (
                <span className="text-red-500 text-sm">
                  {errors.name.message}
                </span>
              )}
            </div>

            {/* Price */}
            <div className="flex-1 flex flex-col gap-1 w-full sm:max-w-[230px]">
              <label className="text-labelText font-semibold">
                Cup of Gratitude Price
              </label>
              <input
                type="number"
                placeholder="Enter price"
                {...register("price", {
                  required: "Price is required",
                  pattern: {
                    value: /^\d+(\.\d{1,2})?$/,
                    message: "Enter a valid price (e.g., 2 or 2.50)",
                  },
                })}
                className="w-full h-[51px] px-4 border border-lightGrayBorder rounded-lg outline-none"
              />

              {errors.price && (
                <span className="text-red-500 text-sm">
                  {errors.price.message}
                </span>
              )}
            </div>
          </div>

          {/* Image upload */}
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-labelText font-semibold mb-1">
                  Upload Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  id="imageUpload"
                  className="hidden"
                  onChange={handleImageUpload}
                />
                <label
                  htmlFor="imageUpload"
                  className="cursor-pointer text-forestText w-[145px] h-[50px] flex justify-center items-center bg-paperWhiteBg rounded-full"
                >
                  + Upload
                </label>
              </div>

              {imagePreview && (
                <div className="relative w-12 h-12 mt-7">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover rounded"
                  />
                  <button
                    type="button"
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                    onClick={() => {
                      setImagePreview(null);
                      setSelectedFile(null);
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="mt-4 bg-darkSlateBlueBg text-white px-6 py-2 rounded"
        >
          Save Changes
        </button>
      </form>
    </div>
  );
};

export default CupGratitudeEdit;
